import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { MastheadPortalId } from '../../../models/AdminModels';
import { useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { NavigationBreadcrumbsBar } from '../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../web/components/Portal';
import { UpgradeWizard } from '../../components/tools/UpgradeWizard';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';

const styles = StyleSheet.create({
	accountHeader: {
		backgroundColor: '#f8f9fa',
		borderBottom: '1px solid #e9ecef',
		marginBottom: 16,
		padding: '12px 16px',
	},
	accountNameRow: {
		alignItems: 'center',
		display: 'flex',
		gap: 12,
	},
	accountName: {
		color: '#212529',
		fontSize: 16,
		fontWeight: 600,
		margin: 0,
	},
	accountDetailsLink: {
		color: '#046cb6',
		fontSize: 14,
		textDecoration: 'none',
		':hover': {
			textDecoration: 'underline',
		},
	},
	accountLabel: {
		color: '#6c757d',
		fontSize: 12,
		marginBottom: 4,
	},
	breadcrumbsContainer: {
		alignItems: 'center',
		display: 'flex',
		gap: 12,
	},
	betaBadge: {
		backgroundColor: '#ffc107',
		borderRadius: 4,
		color: '#212529',
		fontSize: 12,
		fontWeight: 600,
		padding: '4px 10px',
		textTransform: 'uppercase' as const,
	},
	accountInstructions: {
		color: '#495057',
		fontSize: 14,
		lineHeight: 1.5,
		marginTop: 8,
	},
});

export const AdminUpgradeWizardPage: React.FC = () => {
	const { accountId } = useParams<{ accountId: string }>();

	const accountQuery = useAdminAccountQuery({
		accountId: accountId || '',
		enabled: !!accountId,
	});

	const accountName = accountQuery.data?.companyName;

	const pathComponentNameProvider = React.useCallback(
		(pathComponent: string) => {
			if (pathComponent === accountId && accountName) {
				return accountName;
			}
			if (pathComponent === 'upgrade-wizard') {
				return 'Upgrade Wizard';
			}
			return undefined;
		},
		[accountId, accountName]
	);

	if (!accountId) {
		return (
			<AdminDashboardPageLayout>
				<div style={{ padding: 24, textAlign: 'center', color: '#721c24' }}>
					{'Account ID is required. Please navigate to an account and select "Upgrade Account" from the menu.'}
				</div>
			</AdminDashboardPageLayout>
		);
	}

	return (
		<AdminDashboardPageLayout>
			<Portal destination={MastheadPortalId}>
				<div className={css(styles.breadcrumbsContainer)}>
					<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
					<span className={css(styles.betaBadge)}>Beta</span>
				</div>
			</Portal>
			{accountName && (
				<div className={css(styles.accountHeader)}>
					<div className={css(styles.accountLabel)}>Upgrading Account</div>
					<div className={css(styles.accountNameRow)}>
						<h2 className={css(styles.accountName)}>{accountName}</h2>
						<NavLink className={css(styles.accountDetailsLink)} to={`/accounts/${accountId}`}>
							Account Details
						</NavLink>
					</div>
					<p className={css(styles.accountInstructions)}>
						Use this tool to add components or change billing cycles for this paid account. Select your upgrade options
						and preview the pricing before executing.
					</p>
				</div>
			)}
			<UpgradeWizard
				accountId={accountId}
				billingReferenceId={accountQuery.data?.planDetails?.billingReferenceId}
				companyName={accountName}
				customContentEnabled={!!accountQuery.data?.features?.customContentCalendar?.enabled}
			/>
		</AdminDashboardPageLayout>
	);
};
